import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Sparkles, Trash2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

type DbError = { message: string };
type DbResult<T = unknown> = { data: T | null; error: DbError | null };
type LooseQuery<T = unknown> = PromiseLike<DbResult<T>> & {
  select: (columns?: string) => LooseQuery<T>;
  eq: (column: string, value: unknown) => LooseQuery<T>;
  in: (column: string, values: unknown[]) => LooseQuery<T>;
  contains: (column: string, value: unknown) => LooseQuery<T>;
  like: (column: string, value: string) => LooseQuery<T>;
  maybeSingle: () => PromiseLike<DbResult<T | null>>;
  single: () => PromiseLike<DbResult<T>>;
  insert: (values: unknown) => LooseQuery<T>;
  update: (values: Record<string, unknown>) => LooseQuery<T>;
  delete: () => LooseQuery<T>;
};
type LooseSupabaseClient = { from: (table: string) => LooseQuery };
type IdRow = { id: string };
type JourneyStatus = "pending" | "accepted" | "ignored" | "remind_later" | "completed";
type OnboardingProfile = {
  onboarding_completed: boolean;
  onboarding_journey_status: JourneyStatus | null;
  onboarding_remind_at: string | null;
};

const onboardingDb = supabase as unknown as LooseSupabaseClient;
const ignoredJourneyStorageKey = (userId: string) => `delapresse:onboarding-journey-ignored:${userId}`;

function isJourneyIgnoredLocally(userId?: string) {
  if (!userId) return false;
  try {
    return localStorage.getItem(ignoredJourneyStorageKey(userId)) === "true";
  } catch {
    return false;
  }
}

function setJourneyIgnoredLocally(userId: string | undefined, ignored: boolean) {
  if (!userId) return;
  try {
    if (ignored) localStorage.setItem(ignoredJourneyStorageKey(userId), "true");
    else localStorage.removeItem(ignoredJourneyStorageKey(userId));
  } catch {
    // Browser storage can be unavailable in private or restricted contexts.
  }
}

type TourTargetRect = { top: number; left: number; width: number; height: number };

const tourIdForPath = (path: string) => `tour-nav-${path.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "") || "home"}`;

const steps = [
  {
    title: "Dashboard",
    body: "Start with Company Status, Pending Reviews, Automation Crashes, Storage, and AI Usage.",
    href: "/dashboard",
    targetPath: "/dashboard",
  },
  {
    title: "Websites",
    body: "Create or import websites for the current company. Growth allows up to three websites before an upgrade message appears.",
    href: "/websites/create",
    targetPath: "/websites",
  },
  {
    title: "Sources",
    body: "Add the feeds, topics, and keywords that power your company automation and editorial queues.",
    href: "/sources",
    targetPath: "/sources",
  },
  {
    title: "Articles",
    body: "Review drafts, improve content, schedule articles, and publish only to websites in this workspace.",
    href: "/articles",
    targetPath: "/articles",
  },
  {
    title: "Media",
    body: "Manage company-scoped images and assets used by your articles, websites, and publishing flow.",
    href: "/media-library",
    targetPath: "/media-library",
  },
  {
    title: "Integrations",
    body: "Connect and monitor providers such as email, billing, AI, media, publishing, and hosting for this company.",
    href: "/integrations",
    targetPath: "/integrations",
  },
  {
    title: "Settings",
    body: "Finish company preferences, account settings, access controls, templates, and workspace defaults.",
    href: "/settings",
    targetPath: "/settings",
  },
];

async function fetchProfile(userId: string) {
  const { data, error } = await onboardingDb
    .from("profiles")
    .select("onboarding_completed,onboarding_journey_status,onboarding_remind_at")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data as OnboardingProfile | null;
}

async function seedDemo(companyId: string, userId: string) {
  const websiteSlug = `demo-newsroom-${Date.now()}`;
  const { data: websiteData, error: websiteError } = await onboardingDb.from("websites").insert({
    company_id: companyId,
    owner_id: userId,
    name: "Demo Newsroom",
    slug: websiteSlug,
    status: "draft",
    niche: "Demo",
    primary_language: "en",
    languages: ["en", "fr"],
    description: "Demo workspace website for exploring Delapresse.",
    tagline: "Demo content, safe to remove",
    content_config: { demo: true },
    technical_config: { demo: true },
  }).select("id").single();
  if (websiteError) throw websiteError;
  const website = websiteData as IdRow;

  const { data: authorData, error: authorError } = await onboardingDb.from("authors").insert([
    { company_id: companyId, name: "Demo Editor", language: "en", bio: "Demo editorial author." },
    { company_id: companyId, name: "Demo Analyst", language: "fr", bio: "Demo analysis author." },
  ]).select("id");
  if (authorError) throw authorError;
  const authors = (authorData ?? []) as IdRow[];

  const { data: sourceData, error: sourceError } = await onboardingDb.from("news_sources").insert({
    company_id: companyId,
    user_id: userId,
    owner_id: userId,
    topic: "Demo newsroom source",
    keywords: ["demo", "newsroom", "automation"],
    language: "en",
    is_active: false,
  }).select("id").single();
  if (sourceError) throw sourceError;
  const source = sourceData as IdRow;

  const { data: articleData, error: articleError } = await onboardingDb.from("articles").insert([
    { company_id: companyId, owner_id: userId, author_id: userId, editorial_author_id: authors?.[0]?.id, title: "Demo: Editorial Workflow Overview", slug: `demo-editorial-workflow-${Date.now()}`, status: "draft", content: "Demo article for onboarding.", tags: ["Demo"] },
    { company_id: companyId, owner_id: userId, author_id: userId, editorial_author_id: authors?.[1]?.id, title: "Demo: Automation Queue Sample", slug: `demo-automation-queue-${Date.now()}`, status: "review", content: "Demo article for review.", tags: ["Demo"] },
    { company_id: companyId, owner_id: userId, author_id: userId, editorial_author_id: authors?.[0]?.id, title: "Demo: Publishing Checklist", slug: `demo-publishing-checklist-${Date.now()}`, status: "scheduled", content: "Demo scheduled article.", tags: ["Demo"] },
  ]).select("id");
  if (articleError) throw articleError;
  const articles = (articleData ?? []) as IdRow[];

  await onboardingDb.from("article_publications").insert(articles.map((article) => ({
    article_id: article.id,
    website_id: website.id,
    status: "draft",
  })));

  await onboardingDb.from("automation_logs").insert({
    company_id: companyId,
    source_id: source.id,
    step: "demo_seed",
    status: "success",
    details: { demo: true },
  });
}

async function removeDemo(companyId: string) {
  const { data: demoWebsites } = await onboardingDb.from("websites").select("id").eq("company_id", companyId).eq("niche", "Demo");
  const websiteIds = ((demoWebsites ?? []) as IdRow[]).map((item) => item.id);
  if (websiteIds.length) {
    await onboardingDb.from("article_publications").delete().in("website_id", websiteIds);
    await onboardingDb.from("websites").delete().in("id", websiteIds);
  }
  await onboardingDb.from("articles").delete().eq("company_id", companyId).contains("tags", ["Demo"]);
  await onboardingDb.from("authors").delete().eq("company_id", companyId).like("name", "Demo%");
  await onboardingDb.from("news_sources").delete().eq("company_id", companyId).eq("topic", "Demo newsroom source");
  await onboardingDb.from("automation_logs").delete().eq("company_id", companyId).eq("step", "demo_seed");
}

export default function OnboardingTour() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const [stepIndex, setStepIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [localJourneyStatus, setLocalJourneyStatus] = useState<JourneyStatus | null>(null);
  const [targetRect, setTargetRect] = useState<TourTargetRect | null>(null);
  const { data: profile } = useQuery({
    queryKey: ["onboarding-profile", user?.id],
    queryFn: () => fetchProfile(user!.id),
    enabled: !!user,
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;
      const { error } = await onboardingDb
        .from("profiles")
        .update({ onboarding_completed: true, onboarding_journey_status: "completed", onboarding_remind_at: null })
        .eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-profile", user?.id] });
      setDismissed(true);
    },
  });

  const choiceMutation = useMutation({
    mutationFn: async (choice: "accepted" | "ignored" | "remind_later") => {
      if (!user) return;
      const patch = choice === "remind_later"
        ? {
            onboarding_journey_status: "remind_later",
            onboarding_remind_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          }
        : {
            onboarding_journey_status: choice,
            onboarding_remind_at: null,
            onboarding_completed: choice === "accepted" ? false : true,
          };
      const { error } = await onboardingDb.from("profiles").update(patch).eq("id", user.id);
      if (error) throw error;
    },
    onMutate: (choice) => {
      if (choice === "ignored") {
        setJourneyIgnoredLocally(user?.id, true);
        setDismissed(true);
      } else {
        setJourneyIgnoredLocally(user?.id, false);
      }
      setLocalJourneyStatus(choice);
      if (choice === "accepted") {
        setDismissed(false);
        setStepIndex(0);
      }
    },
    onSuccess: (_data, choice) => {
      const nextProfile: OnboardingProfile = {
        onboarding_completed: choice !== "accepted",
        onboarding_journey_status: choice,
        onboarding_remind_at: choice === "remind_later" ? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() : null,
      };
      queryClient.setQueryData(["onboarding-profile", user?.id], (current: OnboardingProfile | null | undefined) => ({
        ...(current ?? {}),
        ...nextProfile,
      }));
      queryClient.invalidateQueries({ queryKey: ["onboarding-profile", user?.id] });
      if (choice === "ignored" || choice === "remind_later") setDismissed(true);
    },
    onError: (error: Error, choice) => {
      if (choice !== "ignored") {
        setLocalJourneyStatus(null);
      }
      toast.error(choice === "ignored" ? "Journey hidden on this browser. Could not save the preference to your profile." : (error.message || "Could not save onboarding choice"));
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => seedDemo(currentCompanyId!, user!.id),
    onSuccess: () => {
      toast.success("Demo data added");
      queryClient.invalidateQueries();
    },
    onError: (error: Error) => toast.error(error.message || "Could not seed demo data"),
  });

  const removeMutation = useMutation({
    mutationFn: () => removeDemo(currentCompanyId!),
    onSuccess: () => {
      toast.success("Demo data removed");
      queryClient.invalidateQueries();
    },
    onError: (error: Error) => toast.error(error.message || "Could not remove demo data"),
  });

  const journeyStatus = isJourneyIgnoredLocally(user?.id) ? "ignored" : localJourneyStatus ?? profile?.onboarding_journey_status ?? "pending";
  const journeyIgnored = journeyStatus === "ignored";
  const journeyCompleted = journeyIgnored || (Boolean(profile?.onboarding_completed) && journeyStatus !== "accepted");
  const remindAt = profile?.onboarding_remind_at ? new Date(profile.onboarding_remind_at).getTime() : 0;
  const shouldAsk =
    !journeyStatus ||
    journeyStatus === "pending" ||
    (journeyStatus === "remind_later" && remindAt <= Date.now());
  const activeJourney = journeyStatus === "accepted";
  const current = steps[stepIndex];
  const currentTargetId = tourIdForPath(current.targetPath);

  useEffect(() => {
    if (!activeJourney) return;
    let activeTarget: HTMLElement | null = null;
    const activeClasses = ["relative", "z-[60]", "ring-2", "ring-primary", "ring-offset-2", "ring-offset-sidebar", "bg-sidebar-accent", "shadow-lg"];
    const updateTarget = () => {
      const target = document.querySelector<HTMLElement>(`[data-tour-id="${currentTargetId}"]`);
      if (!target) {
        setTargetRect(null);
        return;
      }
      if (activeTarget && activeTarget !== target) {
        activeTarget.classList.remove(...activeClasses);
      }
      activeTarget = target;
      target.classList.add(...activeClasses);
      const rect = target.getBoundingClientRect();
      setTargetRect({
        top: Math.max(8, rect.top - 6),
        left: Math.max(8, rect.left - 6),
        width: rect.width + 12,
        height: rect.height + 12,
      });
    };

    updateTarget();
    window.addEventListener("resize", updateTarget);
    window.addEventListener("scroll", updateTarget, true);
    return () => {
      activeTarget?.classList.remove(...activeClasses);
      window.removeEventListener("resize", updateTarget);
      window.removeEventListener("scroll", updateTarget, true);
    };
  }, [activeJourney, currentTargetId]);

  const panelStyle = useMemo(() => {
    if (!targetRect) return { bottom: 20, right: 20 };
    const panelWidth = 380;
    const gap = 18;
    const hasRightSpace = targetRect.left + targetRect.width + gap + panelWidth < window.innerWidth;
    const left = hasRightSpace
      ? targetRect.left + targetRect.width + gap
      : Math.max(16, Math.min(window.innerWidth - panelWidth - 16, targetRect.left));
    const top = Math.max(16, Math.min(window.innerHeight - 340, targetRect.top));
    return { left, top, width: panelWidth };
  }, [targetRect]);

  if (!user || !currentCompanyId || dismissed || journeyCompleted) return null;
  if (!shouldAsk && !activeJourney) return null;

  if (shouldAsk) {
    return (
      <div className="fixed bottom-5 right-5 z-50 w-[380px] rounded-lg border border-border bg-card p-4 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Start your workspace journey?</p>
            <p className="mt-1 text-xs text-muted-foreground">
              A short walkthrough can show the main company workspace areas, package limits, templates, team tools, and publishing flow.
            </p>
          </div>
        </div>
        <div className="mt-4 grid gap-2">
          <Button size="sm" onClick={() => choiceMutation.mutate("accepted")} disabled={choiceMutation.isPending}>
            Yes, I want a journey
          </Button>
          <Button size="sm" variant="outline" onClick={() => choiceMutation.mutate("ignored")} disabled={choiceMutation.isPending}>
            No, ignore the journey
          </Button>
          <Button size="sm" variant="ghost" onClick={() => choiceMutation.mutate("remind_later")} disabled={choiceMutation.isPending}>
            Remind me later
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-slate-950/70 backdrop-blur-[1px]" aria-hidden="true" />
      {targetRect && (
        <div
          className="pointer-events-none fixed z-50 rounded-xl border-2 border-primary bg-background/10 shadow-[0_0_0_9999px_rgba(2,6,23,0.18),0_0_34px_rgba(37,99,235,0.42)] ring-4 ring-primary/20"
          style={targetRect}
          aria-hidden="true"
        />
      )}
      <div
        className="fixed z-50 w-[380px] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-card p-4 shadow-2xl"
        style={panelStyle}
      >
      <div className="flex items-start justify-between gap-3">
        <div className="flex gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Step {stepIndex + 1} of {steps.length}</p>
            <p className="text-sm font-semibold text-foreground">{current.title}</p>
            <p className="mt-1 text-xs text-muted-foreground">{current.body}</p>
          </div>
        </div>
        <button onClick={() => completeMutation.mutate()} className="text-muted-foreground hover:text-foreground" title="Ignore journey">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex gap-1">
          {steps.map((step, index) => (
            <button key={step.title} onClick={() => setStepIndex(index)} className={`h-1.5 rounded-full ${index === stepIndex ? "w-5 bg-primary" : "w-1.5 bg-muted"}`} />
          ))}
        </div>
        <Button asChild size="sm" variant="outline">
          <Link to={current.href}>Open</Link>
        </Button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button size="sm" variant="outline" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
          {seedMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
          Add demo
        </Button>
        <Button size="sm" variant="outline" onClick={() => removeMutation.mutate()} disabled={removeMutation.isPending}>
          {removeMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
          Remove demo
        </Button>
      </div>

      <div className="mt-3 flex justify-between">
        <Button size="sm" variant="ghost" onClick={() => completeMutation.mutate()}>Skip</Button>
        <Button size="sm" variant="ghost" disabled={stepIndex === 0} onClick={() => setStepIndex((value) => Math.max(0, value - 1))}>Back</Button>
        {stepIndex === steps.length - 1 ? (
          <Button size="sm" onClick={() => completeMutation.mutate()}>Finish</Button>
        ) : (
          <Button size="sm" onClick={() => setStepIndex((value) => Math.min(steps.length - 1, value + 1))}>Next</Button>
        )}
      </div>
      </div>
    </>
  );
}
